/**
 * PdfService - lightweight wrapper around pdfmake for generating PDFs in-browser.
 *
 * Loads pdfmake from CDN on first use to avoid increasing bundle size and to work
 * without changing package.json. Uses the default vfs fonts (Roboto) bundled by pdfmake.
 *
 * Usage:
 *   import { PdfService } from './PdfService';
 *   await PdfService.generateSampleReport(sampleJson);
 *
 * Junior-friendly notes:
 * - Keep documentDefinition simple and readable.
 * - This service is UI-agnostic: call it from components or Vuex actions.
 * - No API calls here; this runs entirely in the browser.
 */

import { Capacitor } from '@capacitor/core';
import { openLocalPdf } from '@/Services/docOpeners';
import { validateDocumentModel } from '@/Services/pdf/validateModel';

// Simple debug logger for PDF pipeline. Toggle with window.PDF_DEBUG (true/false)
function pdfDebugEnabled() {
  try { return typeof window !== 'undefined' ? (window.PDF_DEBUG !== false) : false; } catch (_) { return false; }
}
function pdfLog(step, payload) {
  if (!pdfDebugEnabled()) return;
  try {
    const ts = new Date().toISOString();
    // eslint-disable-next-line no-console
    console.log(`[PDF] ${ts} — ${step}`, payload || '');
  } catch (_) {}
}

const CDN_PDFMAKE = "https://cdn.jsdelivr.net/npm/pdfmake@0.2.10/build/pdfmake.min.js";
const CDN_VFS_FONTS = "https://cdn.jsdelivr.net/npm/pdfmake@0.2.10/build/vfs_fonts.js";

/**
 * Load an image (png/jpg/svg) and return a data URL suitable for pdfmake { image: ... }.
 * Handles cross-origin by using fetch+blob; if that fails, attempts img+canvas.
 * @param {string} url
 * @returns {Promise<string>} data URL (e.g., "data:image/png;base64,...")
 */
async function loadImageAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    // Fallback path using <img> + canvas (may be blocked by CORS tainting)
    // eslint-disable-next-line no-console
    console.warn('fetch→blob failed, trying img/canvas fallback for', url, e);
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (err) { reject(err); }
      };
      img.onerror = reject;
      img.src = url + (url.includes('?') ? '&' : '?') + 'cb=' + Date.now();
    });
  }
}

/**
 * Create a simple branded badge as a PNG data URL using Canvas. Used as a fallback demo logo.
 * @param {string} text
 * @returns {Promise<string>}
 */
async function makeDemoBadgeDataUrl(text = 'Lyt‑ning') {
  const w = 260, h = 60, r = 5;
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  // Background (token-like neutral)
  ctx.fillStyle = '#F3F4F6';
  ctx.fillRect(0, 0, w, h);
  // Border radius cap 5px
  ctx.fillStyle = '#339CFF';
  ctx.beginPath();
  ctx.moveTo(r, 0); ctx.lineTo(w - r, 0); ctx.quadraticCurveTo(w, 0, w, r);
  ctx.lineTo(w, h - r); ctx.quadraticCurveTo(w, h, w - r, h);
  ctx.lineTo(r, h); ctx.quadraticCurveTo(0, h, 0, h - r);
  ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.globalAlpha = 0.08; ctx.fill(); ctx.globalAlpha = 1;
  // Text
  ctx.fillStyle = '#111827';
  ctx.font = '600 20px Montserrat, Arial, sans-serif';
  ctx.textBaseline = 'middle';
  const tw = ctx.measureText(text).width;
  ctx.fillText(text, Math.max(12, (w - tw) / 2), h / 2);
  return canvas.toDataURL('image/png');
}

/**
 * Ensure pdfMake is loaded globally. Subsequent calls are no-ops.
 * @returns {Promise<typeof pdfMake>} global pdfMake
 */
async function ensurePdfMake() {
  if (typeof window !== 'undefined' && window.pdfMake) {
    pdfLog('ensurePdfMake: cache-hit (global pdfMake present)');
    return window.pdfMake;
  }
  pdfLog('ensurePdfMake: start loading scripts', { CDN_PDFMAKE, CDN_VFS_FONTS });

  // Helper to load a script once
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      // If already present, resolve immediately
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        // In case it already loaded
        if (existing.getAttribute('data-loaded') === 'true') return resolve();
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => { s.setAttribute('data-loaded', 'true'); resolve(); };
      s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(s);
    });
  }

  await loadScript(CDN_PDFMAKE);
  pdfLog('ensurePdfMake: pdfmake loaded');
  await loadScript(CDN_VFS_FONTS);
  pdfLog('ensurePdfMake: vfs_fonts loaded');
  if (!window.pdfMake) throw new Error('pdfMake failed to initialize');
  pdfLog('ensurePdfMake: initialized');
  return window.pdfMake;
}

/**
 * Convert a plain number to money with two decimals.
 * Use utils.js $utils.formatMoney in components; duplicated minimally here for demo purposes only.
 * @param {number} v
 */
function formatMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const PdfService = {
  /**
   * Render a DocumentModel (JSON) to a PDF Blob using pdfmake.
   * Minimal mapper: accepts either a pdfmake-ready docDefinition or our lightweight DocumentModel
   * supporting blocks: text, stack, columns, image (dataUrl), grid (alias to pdfmake table), pageBreak.
   *
   * @param {Object} model - DocumentModel-like JSON
   * @returns {Promise<Blob>}
   */
  async render(model) {
    pdfLog('render: start', { hasContent: Array.isArray(model?.content), contentLength: model?.content?.length });
    // Dev-time schema validation (no-op in production)
    await validateDocumentModel(model);
    pdfLog('render: validation passed');
    const pdfMake = await ensurePdfMake();
    pdfLog('render: pdfmake ready');
    let def = mapDocumentModelToPdfMake(model);
    pdfLog('render: mapped to docDefinition', { pagesize: def.pageSize, contentCount: def.content?.length });
    def = sanitizeDocDefinition(def);
    def = deepStripLinks(def);
    pdfLog('render: sanitized docDefinition');
    return await new Promise((resolve, reject) => {
      try {
        pdfLog('render: creating pdf');
        let pdf;
        try {
          pdf = pdfMake.createPdf(def);
        } catch (e1) {
          console.error('[PDF] createPdf failed, applying simplify fallback (render)', e1);
          def = simplifyDocDefinition(def);
          pdf = pdfMake.createPdf(def);
        }
        pdf.getBlob(blob => resolve(blob));
      } catch (e) { reject(e); }
    });
  },

  /**
   * Download a PDF file generated from the given model with the specified filename.
   * @param {Object} model
   * @param {string} filename
   */
  async download(model, filename = 'document.pdf') {
    pdfLog('download: start', { filename });
    await validateDocumentModel(model);
    pdfLog('download: validation passed');
    const pdfMake = await ensurePdfMake();
    pdfLog('download: pdfmake ready');
    let def = mapDocumentModelToPdfMake(model);
    pdfLog('download: mapped to docDefinition', { contentCount: def.content?.length });
    def = sanitizeDocDefinition(def);
    def = deepStripLinks(def);
    pdfLog('download: sanitized docDefinition');
    // Android (Capacitor): Save and open natively instead of relying on browser download handling
    try {
      const plat = Capacitor?.getPlatform?.() || 'web';
      if (plat === 'android') {
        pdfLog('download: android native open');
        await new Promise((resolve, reject) => {
          try {
            let pdf;
            try { pdf = pdfMake.createPdf(def); }
            catch (e1) { def = simplifyDocDefinition(def); pdf = pdfMake.createPdf(def); }
            pdf.getBlob(async (blob) => {
              try { await openLocalPdf(blob, filename); resolve(); }
              catch (err) { reject(err); }
            });
          } catch (e) { reject(e); }
        });
        return;
      }
    } catch (_) { /* ignore, fall through to web */ }

    // Web/Electron: standard download
    await new Promise((resolve, reject) => {
      try {
        pdfLog('download: creating and downloading pdf', { filename });
        try {
          pdfMake.createPdf(def).download(filename, resolve);
        } catch (e1) {
          console.error('[PDF] createPdf failed, applying simplify fallback (download)', e1);
          def = simplifyDocDefinition(def);
          pdfMake.createPdf(def).download(filename, resolve);
        }
      } catch (e) { reject(e); }
    });
  },

  /**
   * Open the generated PDF in a new tab.
   * @param {Object} model
   */
  async openInNewTab(model) {
    pdfLog('openInNewTab: start');
    await validateDocumentModel(model);
    pdfLog('openInNewTab: validation passed');
    const pdfMake = await ensurePdfMake();
    pdfLog('openInNewTab: pdfmake ready');
    let def = mapDocumentModelToPdfMake(model);
    pdfLog('openInNewTab: mapped to docDefinition', { contentCount: def.content?.length });
    def = sanitizeDocDefinition(def);
    def = deepStripLinks(def);
    pdfLog('openInNewTab: sanitized docDefinition');
    // Android (Capacitor): write + open with native viewer
    try {
      const plat = Capacitor?.getPlatform?.() || 'web';
      if (plat === 'android') {
        const fname = (model && (model.meta?.title || model.info?.title)) ? `${String(model.meta?.title || model.info?.title)}.pdf` : 'report.pdf';
        pdfLog('openInNewTab: android native open', { filename: fname });
        await new Promise((resolve, reject) => {
          try {
            let pdf;
            try { pdf = pdfMake.createPdf(def); }
            catch (e1) { console.error('[PDF] createPdf failed, applying simplify fallback (open-android)', e1); def = simplifyDocDefinition(def); pdf = pdfMake.createPdf(def); }
            pdf.getBlob(async (blob) => {
              try { await openLocalPdf(blob, fname); resolve(); }
              catch (err) { reject(err); }
            });
          } catch (e) { reject(e); }
        });
        return;
      }
    } catch (_) { /* ignore and use web path */ }

    await new Promise((resolve, reject) => {
      try {
        pdfLog('openInNewTab: creating pdf blob');
        const openBlob = (definition) => {
          let pdf;
          try {
            pdf = pdfMake.createPdf(definition);
          } catch (e1) {
            console.error('[PDF] createPdf failed before getBlob, applying simplify fallback (open)', e1);
            definition = simplifyDocDefinition(definition);
            pdf = pdfMake.createPdf(definition);
          }
          pdf.getBlob((blob) => {
            try {
              const url = URL.createObjectURL(blob);
              const w = window.open(url, '_blank', 'noopener');
              // Revoke after a short delay to allow the browser to start loading
              setTimeout(() => URL.revokeObjectURL(url), 10000);
              resolve(w);
            } catch (err) {
              reject(err);
            }
          });
        };
        openBlob(def);
      } catch (e) { reject(e); }
    });
  },
  /**
   * Generate and open a simple, professional-looking PDF from provided JSON data.
   * Supports optional images and a header logo.
   *
   * @param {Object} data - Input JSON with optional fields:
   *   { title, company: { name, logoUrl, logoDataUrl }, items:[], images:[{url?, dataUrl?, width?, height?, caption?}] }
   * @param {string} [action='open'] - 'open' (new tab), 'download', or 'print'
   * @returns {Promise<void>}
   */
  async generateSampleReport(data = {}, action = 'open') {
    const pdfMake = await ensurePdfMake();

    const now = new Date();
    const title = data.title || 'Sample Report';
    const company = data.company || { name: 'Lyt‑ning Analytics', address: '—' };
    const items = Array.isArray(data.items) && data.items.length ? data.items : [
      { name: 'Widget', qty: 2, unitPrice: 49.9 },
      { name: 'Gadget', qty: 1, unitPrice: 149.0 },
      { name: 'Service', qty: 3, unitPrice: 29.0 },
    ];

    // Prepare optional header logo (prefer provided dataUrl, else load from URL)
    let headerLogoDataUrl = company.logoDataUrl || null;
    if (!headerLogoDataUrl && company.logoUrl) {
      try { headerLogoDataUrl = await loadImageAsDataUrl(company.logoUrl); } catch(_) { headerLogoDataUrl = null; }
    }
    // Fallback demo badge to prove images work, if neither provided
    if (!headerLogoDataUrl) {
      try { headerLogoDataUrl = await makeDemoBadgeDataUrl(company.name || 'L⚡t‑ning'); } catch(_) {}
    }

    // Normalize images array
    const inputImages = Array.isArray(data.images) ? data.images : [];
    const resolvedImages = [];
    for (const img of inputImages) {
      try {
        if (img && img.dataUrl) {
          resolvedImages.push({ dataUrl: img.dataUrl, width: img.width, height: img.height, caption: img.caption });
        } else if (img && img.url) {
          const dataUrl = await loadImageAsDataUrl(img.url);
          resolvedImages.push({ dataUrl, width: img.width, height: img.height, caption: img.caption });
        }
      } catch (e) {
        // Skip images that fail to load to keep PDF generation robust
        // eslint-disable-next-line no-console
        console.warn('Image skipped (failed to load):', img && (img.url || img.caption), e);
      }
    }

    const rows = [
      [{ text: 'Description', style: 'th' }, { text: 'Qty', style: 'th', alignment: 'right' }, { text: 'Unit', style: 'th', alignment: 'right' }, { text: 'Line', style: 'th', alignment: 'right' }],
      ...items.map(i => [
        { text: i.name },
        { text: String(i.qty), alignment: 'right' },
        { text: formatMoney(i.unitPrice), alignment: 'right' },
        { text: formatMoney((i.qty || 0) * (i.unitPrice || 0)), alignment: 'right' },
      ])
    ];

    const contentBlocks = [
      { text: title, style: 'h1', margin: [0, 0, 0, 8] },
      { text: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(now), style: 'muted', margin: [0, 0, 0, 16] },
      {
        layout: 'lightHorizontalLines',
        table: { headerRows: 1, widths: ['*', 40, 60, 60], body: rows }
      },
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 200,
            layout: 'noBorders',
            table: {
              body: [
                [{ text: 'Subtotal', alignment: 'right', style: 'muted' }, { text: formatMoney(items.reduce((s,i)=> s + (i.qty||0)*(i.unitPrice||0), 0)), alignment: 'right' }],
              ]
            },
            margin: [0, 12, 0, 0]
          }
        ]
      }
    ];

    // Optional gallery section to demonstrate images
    if (resolvedImages.length) {
      contentBlocks.push({ text: 'Images', style: 'h2', margin: [0, 18, 0, 8] });
      // Render images as a grid of up to 2 columns
      const grid = [];
      for (let i = 0; i < resolvedImages.length; i += 2) {
        const left = resolvedImages[i];
        const right = resolvedImages[i + 1];
        grid.push({
          columns: [
            left ? { stack: [ { image: left.dataUrl, fit: left.width ? [left.width, left.height || left.width] : [180, 120] }, left.caption ? { text: left.caption, style: 'muted', margin: [0, 4, 0, 0] } : {} ] } : { text: '' },
            right ? { stack: [ { image: right.dataUrl, fit: right.width ? [right.width, right.height || right.width] : [180, 120] }, right.caption ? { text: right.caption, style: 'muted', margin: [0, 4, 0, 0] } : {} ] } : { text: '' },
          ],
          columnGap: 16,
          margin: [0, 0, 0, 12]
        });
      }
      contentBlocks.push(...grid);
    }

    const dd = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      defaultStyle: { fontSize: 10 },
      header: {
        columns: headerLogoDataUrl ? [
          { image: headerLogoDataUrl, fit: [140, 60] },
          { text: title, alignment: 'right', style: 'headerTitle' }
        ] : [
          { text: company.name, style: 'brand' },
          { text: title, alignment: 'right', style: 'headerTitle' }
        ],
        margin: [40, 20, 40, 0]
      },
      footer: function(currentPage, pageCount) {
        return {
          columns: [
            { text: `© ${new Intl.DateTimeFormat(undefined, { year: 'numeric' }).format(now)} ${company.name}`, opacity: 0.6 },
            { text: `${currentPage} / ${pageCount}`, alignment: 'right', opacity: 0.6 }
          ],
          margin: [40, 0, 40, 20]
        };
      },
      content: contentBlocks,
      styles: {
        brand: { fontSize: 10, bold: true },
        headerTitle: { fontSize: 10, color: '#666' },
        h1: { fontSize: 16, bold: true },
        h2: { fontSize: 12, bold: true },
        th: { bold: true, color: '#444' },
        muted: { color: '#666' }
      }
    };

    // Android: get Blob and open natively to avoid blank WebView tabs
    const platform = Capacitor.getPlatform?.() || 'web';
    if (platform === 'android') {
      await new Promise((resolve, reject) => {
        pdfMake.createPdf(dd).getBlob(async (blob) => {
          try { await openLocalPdf(blob, `${title}.pdf`); resolve(); } catch (e) { reject(e); }
        });
      });
      return;
    }

    const doc = pdfMake.createPdf(dd);
    switch (action) {
      case 'download': doc.download(`${title}.pdf`); break;
      case 'print': doc.print(); break;
      default: doc.open(); break; // opens in a new tab
    }
  }
};

export default PdfService;


/**
 * Print a standardized table report via pdfmake.
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} [opts.subtitle]
 * @param {Array<string|Object>} opts.columns - strings or { key,label,align?, width? }
 * @param {Array<Object|Array>} opts.rows - objects (by key) or arrays (by column order)
 * @param {Object} [opts.company] - { name, logoUrl, logoDataUrl }
 * @param {'open'|'download'|'print'} [opts.action='open']
 * @param {string} [opts.filename]
 */
// Header sync using NavBar's persisted state (localStorage)
function getNavHeaderSync(currentStoreName, providedStoreDb) {
  try {
    const selRaw = localStorage.getItem('selected_store');
    const storesRaw = localStorage.getItem('stores');
    const selected = selRaw ? JSON.parse(selRaw) : null;
    const stores = storesRaw ? JSON.parse(storesRaw) : [];

    if (currentStoreName && providedStoreDb) return { storeName: currentStoreName, storeDb: providedStoreDb };

    let storeName = currentStoreName;
    let storeDb = providedStoreDb;

    if (!storeName || storeName === 'All') {
      if (selected && selected.StoreName && selected.StoreName !== 'All') {
        storeName = selected.StoreName;
        if (!storeDb) storeDb = selected.StoreDB || undefined;
      } else {
        storeName = 'Across all stores';
        if (!storeDb && Array.isArray(stores) && stores.length) {
          const main = stores.find(s => s && (s.mainBranch === true || s.Main === true));
          if (main && main.StoreDB) storeDb = main.StoreDB;
        }
      }
    } else if (!storeDb && Array.isArray(stores)) {
      const match = stores.find(s => s && s.StoreName === storeName);
      if (match && match.StoreDB) storeDb = match.StoreDB;
    }

    return { storeName, storeDb };
  } catch (e) {
    return { storeName: currentStoreName, storeDb: providedStoreDb };
  }
}

function buildStoreLogoUrl(storeDb, baseOverride) {
  if (!storeDb) return '';
  const base = (baseOverride || window.stocklistApi || '').replace(/\/$/, '/');
  return `${base}settings/${encodeURIComponent(storeDb)}/logo.png`;
}

async function fetchImageAsDataURL(url) {
  if (!url) return null;
  try { return await loadImageAsDataUrl(url + (url.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`)); } catch (_) { return null; }
}

function pickOrientation(columns) {
  const threshold = 6; // landscape if many columns
  return (Array.isArray(columns) && columns.length > threshold) ? 'landscape' : 'portrait';
}

function computeA4Widths(columnCount) { return Array(Math.max(1, columnCount || 1)).fill('*'); }

export async function printTableReport(opts = {}) {
  // Deprecated path. Use reportToDoc adapter + Vuex `pdf` actions (pdf/download or pdf/open).
  throw new Error('Deprecated: printTableReport has been removed. Use reportToDoc adapter + Vuex pdf actions (pdf/download or pdf/open).');
  const pdfMake = await ensurePdfMake();
  const now = new Date();

  // New: visual preset + metadata support
  const preset = opts.preset || 'reportV1'; // 'reportV1' | 'default'
  const meta = opts.meta || {}; // { rangeText, storeText, printedText }

  const title = opts.title || 'Report';
  const subtitle = opts.subtitle || '';
  const action = opts.action || 'open';
  const company = opts.company || {};
  const filename = opts.filename || (title.replace(/[^a-z0-9]+/gi, '_') + '.pdf');

  // Columns normalization
  const cols = (opts.columns || []).map(c => {
    if (typeof c === 'string') return { key: c, label: c, align: 'left' };
    const key = c.field || c.key || c.prop || c.name || c.label;
    const label = c.label || c.header || c.title || c.key || c.field || String(key || '');
    const align = c.align || inferAlign({ key, label });
    return { key, label, align, width: c.width };
  });

  // Header sync: use the same store selection as Navigation Bar
  const headerSync = getNavHeaderSync(null, null);
  const storeNameText = headerSync.storeName || '';
  const storeLogoUrl = buildStoreLogoUrl(headerSync.storeDb);

  // Prepare header logo with store logo priority
  let headerLogoDataUrl = null;
  if (storeLogoUrl) {
    headerLogoDataUrl = await fetchImageAsDataURL(storeLogoUrl);
  }
  // Fallback to provided company logo
  if (!headerLogoDataUrl && company.logoDataUrl) headerLogoDataUrl = company.logoDataUrl;
  if (!headerLogoDataUrl && company.logoUrl) {
    try { headerLogoDataUrl = await loadImageAsDataUrl(company.logoUrl); } catch (_) { headerLogoDataUrl = null; }
  }
  // Final fallback: demo badge
  if (!headerLogoDataUrl) {
    try { headerLogoDataUrl = await makeDemoBadgeDataUrl(company.name || 'Lyt‑ning'); } catch (_) {}
  }

  // Build table rows
  const headerRow = cols.map(c => ({ text: c.label, style: 'th', alignment: toAlign(c), noWrap: false }));
  const bodyRows = (opts.rows || []).map(r => {
    if (Array.isArray(r)) {
      const fixed = new Array(cols.length);
      for (let i = 0; i < cols.length; i++) fixed[i] = (i < r.length && r[i] != null) ? r[i] : '';
      return fixed.map((cell, i) => ({ text: toCell(cell, cols[i]), alignment: toAlign(cols[i]), noWrap: false }));
    }
    return cols.map(c => ({ text: toCell(r[c.key], c), alignment: toAlign(c), noWrap: false }));
  });

  // Page orientation: landscape for many columns
  const pageOrientation = pickOrientation(cols);

  // Document by preset
  const dd = (preset === 'reportV1') ? buildReportV1() : buildDefault();

  const platform2 = Capacitor.getPlatform?.() || 'web';
  if (platform2 === 'android') {
    await new Promise((resolve, reject) => {
      pdfMake.createPdf(dd).getBlob(async (blob) => {
        try { await openLocalPdf(blob, filename); resolve(); } catch (e) { reject(e); }
      });
    });
    return;
  }

  const pdf = pdfMake.createPdf(dd);
  if (action === 'download') pdf.download(filename);
  else if (action === 'print') pdf.print();
  else pdf.open();

  // Helpers
  function toCell(val, col) {
    if (val == null) return '';
    // Only auto-format numbers when column looks monetary/amount-like; otherwise keep as-is
    if (typeof val === 'number') {
      const key = ((col && (col.key || col.label)) || '').toString().toLowerCase();
      if (/total|amount|value|sales|revenue|qty|quantity|price|cost/.test(key)) return formatMoney(val);
      return String(val);
    }
    return String(val);
  }
  function toAlign(col) {
    const a = col && col.align; if (a === 'right' || a === 'center') return a; return inferAlign(col);
  }
  function inferAlign(col) {
    const key = ((col && (col.key || col.label)) || '').toString().toLowerCase();
    return /total|amount|value|sales|revenue|qty|quantity|price|cost|percent|rate/.test(key) ? 'right' : 'left';
  }

  function tableBlock(cols, rows, { compact } = {}) {
    const widths = computeA4Widths(cols.length);
    const body = [headerRow, ...rows];

    // Optional Totals row (detect a numeric column with total/amount semantics)
    if (!opts.disableTotals && opts.showTotals !== false && rows.length) {
      const totalIndex = cols.findIndex(c => /total|amount|value|sales|revenue/.test(((c.key||'') + ' ' + (c.label||'')).toLowerCase()));
      if (totalIndex >= 0) {
        const sum = (opts.rows || []).reduce((a, r) => {
          const v = Array.isArray(r) ? r[totalIndex] : r[cols[totalIndex].key];
          const n = Number(String(v).replace(/[^0-9.-]/g, ''));
          return a + (isFinite(n) ? n : 0);
        }, 0);
        // Simple totals row without nested tables/canvas to avoid layout glitches
        const tRow = new Array(cols.length).fill({ text: '' });
        tRow[0] = { text: 'Totals', style: 'totalLabel' };
        tRow[totalIndex] = { text: formatMoney(sum), style: 'totalValue', alignment: 'right' };
        body.push(tRow);
      }
    }

    const tableNode = {
      layout: {
        paddingTop: () => compact ? 6 : 8,
        paddingBottom: () => compact ? 6 : 8,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        fillColor: (rowIndex) => rowIndex === 0 ? '#F3F4F6' : null,
        hLineColor: '#E5E7EB', vLineColor: '#E5E7EB'
      },
      table: { headerRows: 1, widths, body }
    };
    // Render the table directly so pdfmake manages page breaks and header rows reliably
    return tableNode;
  }

  function buildDefault() {
    const subParts = [subtitle].filter(Boolean);
    const printed = new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(now);

    const headerBlock = {
      margin: [34, 16, 34, 8],
      columns: [
        headerLogoDataUrl ? { image: headerLogoDataUrl, fit: [150, 150], margin: [0, 0, 12, 0] } : { text: ' ', width: 0 },
        { width: '*', stack: [
            storeNameText ? { text: storeNameText, style: 'storeName', alignment: 'right' } : null,
            { text: title, style: 'h1', alignment: 'right' },
            subParts.length ? { text: subParts.join(' • '), style: 'muted', alignment: 'right', margin: [0, 2, 0, 2] } : null,
            { text: `Printed: ${printed}`, style: 'muted', alignment: 'right' }
          ].filter(Boolean) }
      ]
    };

    return {
      pageSize: 'A4',
      pageOrientation,
      pageMargins: [34, 34, 34, 42],
      defaultStyle: { fontSize: 9 },
      styles: { h1: { fontSize: 16, bold: true }, storeName: { fontSize: 20, bold: true }, muted: { color: '#6B7280' }, th: { bold: true, fillColor: '#F3F4F6' } },
      footer: (currentPage, pageCount) => ({ margin: [34, 8, 34, 12], columns: [ { text: 'Lyt‑ning Analytics', style: 'muted' }, { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'muted' } ] }),
      content: [ headerBlock, tableBlock(cols, bodyRows, { compact: true }) ]
    };
  }

  function buildReportV1() {
    const tinyDate = meta.printedText || new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(now);
    const rangeText = meta.rangeText || subtitle || '';
    const storeText = meta.storeText || (storeNameText ? `${storeNameText}` : '');

    const headerBlock = {
      margin: [34, 16, 34, 8],
      columns: [
        headerLogoDataUrl ? { image: headerLogoDataUrl, fit: [150, 150], margin: [0, 0, 12, 0] } : { text: ' ', width: 0 },
        { width: '*', stack: [
            storeText ? { text: storeText, style: 'storeName', alignment: 'right' } : null,
            { text: title, style: 'hTitle', alignment: 'right' },
            rangeText ? { text: rangeText, style: 'caption', alignment: 'right', margin: [0, 2, 0, 0] } : null,
            { text: `Printed: ${tinyDate}`, style: 'caption', alignment: 'right' }
          ].filter(Boolean) }
      ]
    };

    return {
      pageSize: 'A4', pageOrientation, pageMargins: [34, 48, 34, 42], defaultStyle: { fontSize: 9 },
      styles: {
        caption: { fontSize: 8, color: '#6B7280' },
        topTitle: { fontSize: 9, color: '#6B7280', alignment: 'center' },
        hTitle: { fontSize: 12, bold: true, alignment: 'right' },
        storeName: { fontSize: 20, bold: true },
        muted: { color: '#6B7280' },
        th: { bold: true, color: '#374151' },
        totalLabel: { bold: true, color: '#374151' },
        totalValue: { bold: true }
      },
      footer: (currentPage, pageCount) => ({ margin: [34, 8, 34, 12], columns: [ { text: 'Lyt‑ning Analytics', style: 'caption' }, { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'caption' } ] }),
      content: [ headerBlock, tableBlock(cols, bodyRows, { compact: true }) ]
    };
  }
}

// DEPRECATED: printTableReport is no longer attached to PdfService. Use reportToDoc adapter + Vuex `pdf` actions instead.

/**
 * Internal: Map our lightweight DocumentModel to pdfmake docDefinition.
 * Always maps blocks so aliases like type:'grid' are converted to pdfmake { table }.
 * If the input already resembles a full pdfmake doc (pageSize/pageMargins/etc.), preserve those fields.
 */
function mapDocumentModelToPdfMake(model) {
  pdfLog('mapDocumentModelToPdfMake: start');
  if (!model || typeof model !== 'object') {
    pdfLog('mapDocumentModelToPdfMake: empty model');
    return { content: [{ text: 'Empty document' }] };
  }
  
  // If caller passed a raw pdfmake block (rare), wrap it
  if (model.table || model.stack || model.columns || model.text) {
    const def = { content: [model] };
    pdfLog('mapDocumentModelToPdfMake: raw node wrapped');
    return def;
  }

  const hasPdfMakeDocProps = !!(model.pageSize || model.pageMargins || model.defaultStyle || model.styles || model.header || model.footer);
  const hasContent = Array.isArray(model.content);

  // Build a unified docDefinition, mapping all blocks
  const def = {
    info: model.info || model.meta || undefined,
    pageSize: (model.page && model.page.size) || model.pageSize || 'A4',
    pageMargins: (model.page && model.page.margin) || model.pageMargins || [24, 24, 24, 24],
    pageOrientation: (model.page && model.page.orientation) || model.pageOrientation || 'portrait',
    header: model.header ? mapBlock(model.header) : undefined,
    footer: model.footer ? mapBlock(model.footer) : undefined,
    styles: Object.assign({
      h1: { fontSize: 18, bold: true },
      h2: { fontSize: 14, bold: true },
      th: { bold: true }
    }, model.styles || {}),
    defaultStyle: model.defaultStyle || { fontSize: 10 },
    content: hasContent ? model.content.map(mapBlock) : []
  };

  // If the input already had pdfmake doc properties, keep them as-is where provided
  if (hasPdfMakeDocProps) {
    if (model.header && !def.header && typeof model.header === 'function') def.header = model.header;
    if (model.footer && !def.footer && typeof model.footer === 'function') def.footer = model.footer;
  }

  pdfLog('mapDocumentModelToPdfMake: end', { contentCount: def.content?.length });
  return def;
}

function mapBlock(block) {
  const logType = (t) => pdfLog('mapBlock', { type: t });
  if (Array.isArray(block)) return block.map(mapBlock);
  if (!block || typeof block !== 'object') return { text: String(block || '') };
  switch (block.type) {
    case 'text':
      logType('text');
      return { text: block.text, style: block.style, alignment: block.alignment, margin: block.margin };
    case 'stack':
      logType('stack');
      return { stack: (block.items || []).map(mapBlock), margin: block.margin };
    case 'columns':
      logType('columns');
      return { columns: (block.columns || []).map(mapBlock), columnGap: block.columnGap, margin: block.margin };
    case 'image': {
      logType('image');
      const img = block.imageKey ? (block.imageKey) : block.dataUrl || block.src;
      return { image: img, width: block.width, height: block.height, fit: block.fit, margin: block.margin };
    }
    case 'grid':
      logType('grid');
      return { layout: block.layout || 'lightHorizontalLines', table: { headerRows: block.headerRows || 0, widths: block.widths || 'auto', body: block.body } };
    case 'pageBreak':
      logType('pageBreak');
      return { text: '', pageBreak: 'after' };
    default:
      // If it already looks like a pdfmake node, return as is
      logType('native-or-unknown');
      if (block.table || block.stack || block.columns || block.text) return block;
      return { text: JSON.stringify(block) };
  }
}

/**
 * Sanitize a pdfmake docDefinition to prevent runtime errors from invalid shapes.
 * - Coerces null/undefined primitives to empty text nodes
 * - Normalizes text arrays and converts href→link
 * - Ensures table cells are objects/strings; coerces nulls; aligns row length with widths
 * - Recurses through stack/columns/header/footer/content
 */
function sanitizeDocDefinition(def) {
  pdfLog('sanitize: start');
  if (!def || typeof def !== 'object') return { content: [{ text: '' }] };

  const sanitizeNode = (node) => {
    if (node == null) return { text: '' };
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
      return { text: String(node) };
    }
    if (Array.isArray(node)) return node.map(sanitizeNode);

    // Clone shallow to avoid mutating caller structures
    const out = { ...node };

    // Normalize text nodes and inline runs
    if (out.text !== undefined) {
      if (Array.isArray(out.text)) {
        // Flatten any inline array to a single plain string to avoid complex run edge-cases
        out.text = out.text
          .filter(seg => seg != null)
          .map(seg => {
            if (typeof seg === 'string' || typeof seg === 'number' || typeof seg === 'boolean') return String(seg);
            if (seg && typeof seg === 'object') return seg.text != null ? String(seg.text) : '';
            return '';
          })
          .join('');
      } else if (out.text == null) {
        out.text = '';
      } else if (typeof out.text !== 'string') {
        out.text = String(out.text);
      }
    }

    // Normalize link alias if present at node level, then strip links entirely to avoid pdfmake href issues
    if (out.href && !out.link) out.link = out.href;
    delete out.href;
    // If any link remains, drop it to avoid pdfmake link decoration edge-cases
    if (out.link) delete out.link;

    // Normalize tables
    if (out.table && out.table.body) {
      const tbl = { ...out.table };
      const widths = Array.isArray(tbl.widths) ? tbl.widths : null;
      if (!Array.isArray(tbl.body)) tbl.body = [];
      tbl.body = tbl.body.map(row => {
        const arr = Array.isArray(row) ? row : [];
        let cells = arr.map(c => {
          if (c == null) return { text: '' };
          if (typeof c === 'string' || typeof c === 'number' || typeof c === 'boolean') return { text: String(c) };
          if (Array.isArray(c)) return sanitizeNode(c); // uncommon, but allow nested
          // Fully sanitize object cells (recurse into nested structures and normalize text/link)
          let cell = sanitizeNode(c);
          if (cell && typeof cell === 'object') {
            if (cell.href && !cell.link) cell.link = cell.href;
            delete cell.href;
            // Drop any remaining link on cells to avoid pdfmake href setting on undefined runs
            if (cell.link) delete cell.link;
            if (cell.text == null && !cell.table && !cell.stack && !cell.columns && !cell.image && !cell.qr) {
              cell.text = '';
            } else if (Array.isArray(cell.text)) {
              cell.text = cell.text.filter(x => x != null).map(x => typeof x === 'object' ? (x.text != null ? String(x.text) : '') : String(x)).join('');
            } else if (cell.text != null && typeof cell.text !== 'string') {
              cell.text = String(cell.text);
            }
          }
          return cell;
        });
        // Align to widths length if provided
        if (widths) {
          if (cells.length > widths.length) cells = cells.slice(0, widths.length);
          while (cells.length < widths.length) cells.push({ text: '' });
        }
        return cells;
      });
      // Ensure widths is either an array or undefined (pdfmake expects array for tables)
      if (!Array.isArray(tbl.widths)) delete tbl.widths;
      out.table = tbl;
    }

    // Recurse common containers
    if (Array.isArray(out.stack)) out.stack = out.stack.map(sanitizeNode);
    if (Array.isArray(out.columns)) out.columns = out.columns.map(sanitizeNode);

    return out;
  };

  const sanitized = { ...def };
  if (!Array.isArray(sanitized.content)) sanitized.content = [];
  sanitized.content = sanitized.content.map(sanitizeNode);
  if (sanitized.header && typeof sanitized.header === 'object') sanitized.header = sanitizeNode(sanitized.header);
  if (sanitized.footer && typeof sanitized.footer === 'object') sanitized.footer = sanitizeNode(sanitized.footer);
  pdfLog('sanitize: end', { contentCount: sanitized.content?.length });
  return sanitized;
}

/**
 * Deeply remove any remaining hyperlink-related keys (link, href) anywhere in the docDefinition.
 * Some third-party data can sneak link-like fields into unexpected nodes; stripping them avoids
 * pdfmake attempting to decorate undefined text runs with href.
 */
function deepStripLinks(input) {
  try { pdfLog('sanitize: deepStripLinks'); } catch(_){}
  const seen = new WeakSet();
  const walk = (node) => {
    if (!node || typeof node !== 'object') return node;
    if (seen.has(node)) return node;
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach(walk);
      return node;
    }
    // Remove hyperlink props on this object
    if (Object.prototype.hasOwnProperty.call(node, 'href')) delete node.href;
    if (Object.prototype.hasOwnProperty.call(node, 'link')) delete node.link;
    // Recurse into children
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (v && typeof v === 'object') walk(v);
    }
    return node;
  };
  return walk(input);
}

/**
 * As a last-resort runtime fallback, simplify the docDefinition to a very safe shape:
 * - For tables: coerce every cell to { text: string }
 * - For other nodes: keep only { text } or { image } or nested containers with sanitized children
 */
function simplifyDocDefinition(def) {
  try { pdfLog('sanitize: simplifyDocDefinition'); } catch(_){}
  const clone = (obj) => (obj && typeof obj === 'object' ? JSON.parse(JSON.stringify(obj)) : obj);
  const safeText = (v) => ({ text: v == null ? '' : String(v) });
  const walk = (node) => {
    if (!node || typeof node !== 'object') return safeText(node);
    if (Array.isArray(node)) return node.map(walk);
    // Table: force simple text cells
    if (node.table && Array.isArray(node.table.body)) {
      const widths = Array.isArray(node.table.widths) ? node.table.widths : null;
      const body = node.table.body.map(row => {
        const arr = Array.isArray(row) ? row : [];
        let cells = arr.map(c => safeText(c && typeof c === 'object' && c.text !== undefined ? c.text : c));
        if (widths) {
          if (cells.length > widths.length) cells = cells.slice(0, widths.length);
          while (cells.length < widths.length) cells.push(safeText(''));
        }
        return cells;
      });
      const t = { table: { headerRows: node.table.headerRows || 0, body } };
      if (Array.isArray(node.table.widths)) t.table.widths = node.table.widths;
      return t;
    }
    // Containers
    if (Array.isArray(node.stack)) return { stack: node.stack.map(walk) };
    if (Array.isArray(node.columns)) return { columns: node.columns.map(walk) };
    // Image
    if (node.image) return { image: node.image, width: node.width, height: node.height, fit: node.fit };
    // Text
    if (node.text !== undefined) return safeText(node.text);
    // Unknown object: stringify
    return safeText(JSON.stringify(node));
  };
  const out = clone(def);
  out.content = Array.isArray(out.content) ? out.content.map(walk) : [];
  if (out.header) out.header = walk(out.header);
  if (out.footer) out.footer = walk(out.footer);
  return out;
}
